import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactDOM from 'react-dom';

import { useParams } from 'react-router-dom';
import { HiMinus, HiPlus } from 'react-icons/hi';
import { useResourceGroupResources, useResources } from '~hooks/useResources';
import { useSidebar } from '~contexts/SidebarContext';
import { Icon, Stack, Text } from '~uikit';
import { activeOpacity, hoverHighlight } from '~utils/styled';
import { UnstyledLink } from '~components/navigation/Link';
import { theme } from '~constants/theme';
import { useSubscriptions } from '~hooks/useSubscriptions';
import { useResourceGroups } from '~hooks/useResourceGroups';

export type TreeNode = {
  node: {
    id: string;
    name: string;
    type: string;
    url: string;
    selected: boolean;
  };
  children: TreeNode[];
  depth: number;
};

export default function Sidebar({ mobile }: { mobile?: boolean }) {
  const [sidebarElement, setSidebarElement] = useState<HTMLElement | null>(null); // prettier-ignore

  const params = useParams();

  const { data: subscriptions = [] } = useSubscriptions();
  const { data: resourceGroups = [] } = useResourceGroups(
    params.subscriptionId
  );
  const isResourceRoute = !!(params.siteId || params.deviceRegisterId);
  const { data: allResources = [] } = useResources({
    enabled: !isResourceRoute,
  });
  const { data: scopedResources = [] } = useResourceGroupResources(
    isResourceRoute ? params.subscriptionId : undefined,
    isResourceRoute ? params.resourceGroupId : undefined
  );
  const resources = isResourceRoute ? scopedResources : allResources;

  const subscription = useMemo(() => {
    return subscriptions.find(s => s.subscriptionId === params.subscriptionId);
  }, [params, subscriptions]);

  const tree: TreeNode[] = useMemo(() => {
    if (subscription) {
      return [
        {
          node: {
            id: subscription.id,
            name: subscription.displayName,
            type: 'Subscription',
            url: `/admin/subscriptions/${subscription.subscriptionId}`,
            selected:
              params.subscriptionId === subscription.subscriptionId &&
              !params.resourceGroupId &&
              !params.siteId &&
              !params.deviceRegisterId,
          },
          children: resourceGroups
            .filter(rg => rg.subscriptionId === subscription.subscriptionId)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(rg => ({
              node: {
                id: rg.id,
                name: rg.name,
                type: 'Resource Group',
                url: `/admin${rg.id}`,
                selected:
                  params.resourceGroupId === rg.name &&
                  !params.siteId &&
                  !params.deviceRegisterId,
              },
              children: resources
                .filter(r => r.resourceGroupId === rg.id)
                .sort((a, b) =>
                  (a.displayName ? a.displayName : a.name).localeCompare(
                    b.displayName ? b.displayName : b.name
                  )
                )
                .map(r => ({
                  node: {
                    id: r.id,
                    name: r.displayName ? r.displayName : r.name,
                    type:
                      r.resourceType === 'Cozify.Sites'
                        ? 'Site'
                        : 'Device Register',
                    url:
                      r.resourceType === 'Cozify.Sites'
                        ? `/admin${rg.id}/sites/${r.name}`
                        : `/admin${rg.id}/deviceRegisters/${r.name}`,
                    selected:
                      r.name === params.siteId ||
                      r.name === params.deviceRegisterId,
                  },
                  children: [],
                  depth: 2,
                })),
              depth: 1,
            })),
          depth: 0,
        },
      ];
    }
    return [];
  }, [subscription, resourceGroups, resources, params]);

  const { expandedNodeIds, setNodeExpanded } = useSidebar();

  useEffect(() => {
    // Only resource groups can be expanded from sidebar
    const resourceGroup = resourceGroups.find(
      rg => rg.name === params.resourceGroupId
    );
    if (resourceGroup && !(resourceGroup.id in expandedNodeIds)) {
      setNodeExpanded(resourceGroup.id, true);
    }
  }, [resourceGroups, params, expandedNodeIds, setNodeExpanded]);

  const content = (
    <Wrapper mobile={mobile}>
      <Nav>
        <NavList>
          {tree.map(node => (
            <TreeNode key={node.node.id} node={node} />
          ))}
        </NavList>
      </Nav>
    </Wrapper>
  );

  useLayoutEffect(() => {
    setSidebarElement(document.getElementById('sidebar-slot'));
  }, []);

  if (mobile) return <div>{content}</div>;

  if (!sidebarElement) return null;
  return ReactDOM.createPortal(content, sidebarElement as any);
}

const Wrapper = styled.aside<{ mobile?: boolean }>`
  width: 300px;
  height: 100%;
  background-color: ${p => p.theme.colors.surface};
  border-right: 1px solid ${p => p.theme.colors.border};
  display: flex;
  flex-direction: column;

  ${p =>
    !p.mobile &&
    `
    ${p.theme.media.phone} {
      display: none;
    }
    `}

  ${p => p.mobile && 'width: 100%'}
`;

const Nav = styled.nav`
  flex: 1;
  overflow: auto;
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const NavListItem = styled.div`
  position: relative;
  ${hoverHighlight}
`;

const NavItemLink = styled(UnstyledLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${p => p.theme.colors.text};
  padding: ${p => p.theme.spacing.small}px ${p => p.theme.spacing.normal}px;
  ${activeOpacity}

  &:focus {
    background-color: ${p => p.theme.colors.hoverHighlight};
  }
`;

const StyledExpandButton = styled.button`
  position: relative;
  background-color: ${p => p.theme.colors.muted4};
  border-radius: ${p => p.theme.radii.full}px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${p => p.theme.spacing.xxsmall}px;

  &:hover {
    opacity: 0.8;
  }
`;

function TreeNode({ node }: { node: TreeNode }) {
  const { expandedNodeIds, setNodeExpanded } = useSidebar();

  const isExpanded = expandedNodeIds[node.node.id] || node.depth === 0;

  return (
    <>
      <NavListItem
        style={{
          boxShadow:
            node.depth === 0 ? '0 4px 6px -6px rgb(0 0 0 / 0.2)' : 'none',
          zIndex: node.depth === 0 ? 10 : 1,
          backgroundColor: node.node.selected
            ? theme.colors.primary
            : node.depth !== 0
              ? theme.colors.muted5
              : theme.colors.surface,
        }}
      >
        <NavItemLink
          to={node.node.url}
          preloadOn="click"
          onClick={() => setNodeExpanded(node.node.id, true)}
        >
          <Stack
            axis="x"
            spacing="none"
            justify="space-between"
            align="center"
            fill
          >
            <Stack axis="y" spacing="xxsmall" hideOverflow>
              <Text
                color={node.node.selected ? 'onPrimary' : 'text'}
                variant={node.depth === 0 ? 'bodyStrong' : 'body'}
                style={{
                  marginLeft: node.depth * 15,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {node.node.name}
              </Text>
              <Text
                color={node.node.selected ? 'onPrimary' : 'text'}
                variant="caption"
                style={{
                  marginLeft: node.depth * 15,
                  textTransform: 'uppercase',
                }}
              >
                {node.node.type}
              </Text>
            </Stack>
            {node.depth === 1 && (
              <StyledExpandButton
                type="button"
                onClick={(e: any) => {
                  e.stopPropagation();
                  e.preventDefault();

                  setNodeExpanded(node.node.id, !isExpanded);

                  // Resources are fetched automatically via React Query (useResources).
                }}
              >
                <Icon
                  icon={isExpanded ? HiMinus : HiPlus}
                  size={18}
                  color="text"
                />
              </StyledExpandButton>
            )}
          </Stack>
        </NavItemLink>
      </NavListItem>
      {isExpanded &&
        node.children.map(child => (
          <TreeNode key={child.node.id} node={child} />
        ))}
    </>
  );
}
